import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { Pagination } from '@/types/shared';

/**
 * Reconciliation batches — DataCentral v4 `/reconciliation/batches` endpoints.
 * A batch ("danh sách đối soát") is a draft list of services (+ their invoices)
 * an operator curates before running the pipeline over them.
 * Auth: UMA RPT via `dataCentralApi` (same as all DC v4 data).
 *
 * Envelope shapes:
 *   List:   { message, pagination, results: T[] }
 *   Object: { message, result: T }
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

export type BatchStatusValue = 'draft' | 'ready' | 'running' | 'done' | 'cancelled';

/** One item inside a batch (a service, optionally already invoiced). */
export interface BatchItem {
  service_id: number;
  service_name?: string | null;
  product_group_name?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  company_name?: string | null;
  invoice_id?: number | null;
  invoice_code?: string | null;
  invoice_total?: number | null;
  note?: string | null;
  /** Whether this item participates in the pipeline run. */
  included?: boolean;
}

/** A reconciliation batch (list header). */
export interface Batch {
  id: number;
  name: string;
  status: BatchStatusValue;
  period_start?: string | null;
  period_end?: string | null;
  item_count?: number;
  total_amount?: number;
  created_at?: string | null;
  updated_at?: string | null;
}

/** A batch with its items hydrated (`GET /reconciliation/batches/:id`). */
export interface BatchDetail extends Batch {
  items: BatchItem[];
}

/** One candidate service row from the candidate search. */
export interface Candidate {
  service_id: number;
  service_name?: string | null;
  product_group_name?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  company_name?: string | null;
  invoice_id?: number | null;
  invoice_code?: string | null;
  invoice_total?: number | null;
}

/* ── Request bodies ───────────────────────────────────────────────────────── */

export interface CreateBatchBody {
  name: string;
  period_start?: string | null;
  period_end?: string | null;
}

export interface UpdateBatchBody {
  name?: string;
  status?: BatchStatusValue;
}

export interface AddBatchItemBody {
  service_id: number;
  invoice_id?: number | null;
  customer_id?: number | null;
  note?: string | null;
}

export interface UpdateBatchItemBody {
  note?: string | null;
  included?: boolean;
}

/* ── Params ─────────────────────────────────────────────────────────────── */

export interface BatchesListParams {
  page?: number;
  page_size?: number;
  status?: BatchStatusValue | '';
}

export interface CandidateSearchParams {
  q?: string;
  page?: number;
  page_size?: number;
}

/* ── Internal envelopes ─────────────────────────────────────────────────── */

interface BatchListResponse {
  message: string;
  pagination: Pagination;
  results: Batch[];
}

interface CandidateListResponse {
  message: string;
  pagination: Pagination;
  results: Candidate[];
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const reconciliationBatchKeys = {
  all: ['reconciliation', 'batches'] as const,
  list: (params: BatchesListParams) => ['reconciliation', 'batches', 'list', params] as const,
  detail: (id: number | string) => ['reconciliation', 'batches', 'detail', id] as const,
  candidates: (params: CandidateSearchParams) =>
    ['reconciliation', 'batches', 'candidates', params] as const,
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function cleanQuery(params: Record<string, unknown>): string {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) clean[k] = v as string | number;
  }
  return qs.stringify(clean);
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /reconciliation/batches` — paginated list of batches.
 * Envelope: { message, pagination, results: Batch[] }
 */
export function useBatches(params: BatchesListParams = {}) {
  return useQuery({
    queryKey: reconciliationBatchKeys.list(params),
    queryFn: async (): Promise<BatchListResponse> => {
      const query = cleanQuery({ page: 1, page_size: 20, ...params });
      const res = await dataCentralApi.get<BatchListResponse>(
        `/reconciliation/batches${query ? `?${query}` : ''}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /reconciliation/batches/:id` — one batch + its items.
 * Envelope: { message, result: BatchDetail }
 */
export function useBatch(id: number | string | null, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reconciliationBatchKeys.detail(id ?? 0),
    queryFn: async (): Promise<BatchDetail> => {
      const res = await dataCentralApi.get<{ message: string; result: BatchDetail }>(
        `/reconciliation/batches/${id}`,
      );
      return res.data.result;
    },
    enabled: Boolean(id) && (opts?.enabled ?? true),
  });
}

/**
 * `GET /reconciliation/batches/candidates?q=&page=&page_size=` — searchable
 * pool of services/invoices to add to a batch. Enable from the search box.
 * Envelope: { message, pagination, results: Candidate[] }
 */
export function useBatchCandidates(params: CandidateSearchParams, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reconciliationBatchKeys.candidates(params),
    queryFn: async (): Promise<CandidateListResponse> => {
      const query = cleanQuery({ page: 1, page_size: 10, ...params });
      const res = await dataCentralApi.get<CandidateListResponse>(
        `/reconciliation/batches/candidates${query ? `?${query}` : ''}`,
      );
      return res.data;
    },
    enabled: opts?.enabled ?? true,
  });
}

/**
 * `POST /reconciliation/batches` — create a draft batch.
 * USER must trigger this — never called automatically.
 */
export function useCreateBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBatchBody): Promise<Batch> => {
      const res = await dataCentralApi.post<{ message: string; result: Batch }>(
        `/reconciliation/batches`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.all });
    },
  });
}

/**
 * `PATCH /reconciliation/batches/:id` — rename or change status.
 * USER must trigger this — never called automatically.
 */
export function useUpdateBatch(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBatchBody): Promise<Batch> => {
      const res = await dataCentralApi.patch<{ message: string; result: Batch }>(
        `/reconciliation/batches/${id}`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.all });
    },
  });
}

/**
 * `DELETE /reconciliation/batches/:id` — delete a batch.
 * USER must trigger this — never called automatically.
 */
export function useDeleteBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string): Promise<void> => {
      await dataCentralApi.delete(`/reconciliation/batches/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.all });
    },
  });
}

/**
 * `POST /reconciliation/batches/:id/items` — add one service to a batch.
 * USER must trigger this — never called automatically.
 */
export function useAddBatchItem(batchId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddBatchItemBody): Promise<BatchItem> => {
      const res = await dataCentralApi.post<{ message: string; result: BatchItem }>(
        `/reconciliation/batches/${batchId}/items`,
        body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.detail(batchId) });
    },
  });
}

/**
 * `DELETE /reconciliation/batches/:id/items/:serviceId` — remove one item.
 * USER must trigger this — never called automatically.
 */
export function useRemoveBatchItem(batchId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serviceId: number | string): Promise<void> => {
      await dataCentralApi.delete(`/reconciliation/batches/${batchId}/items/${serviceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.detail(batchId) });
    },
  });
}

/**
 * `PATCH /reconciliation/batches/:id/items/:serviceId` — edit note / included.
 * USER must trigger this — never called automatically.
 */
export function useUpdateBatchItem(batchId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      serviceId: number | string;
      body: UpdateBatchItemBody;
    }): Promise<BatchItem> => {
      const res = await dataCentralApi.patch<{ message: string; result: BatchItem }>(
        `/reconciliation/batches/${batchId}/items/${vars.serviceId}`,
        vars.body,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reconciliationBatchKeys.detail(batchId) });
    },
  });
}
